import { Input, Selector } from '@casumo/dojo-react';
import { ChangeEvent, FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import useFetchErrorByKey from '../api/hooks/useFetchErrorsByKey';
import useValidationLimits from '../api/hooks/useValidationLimits';
import useMarketInfo from '../hooks/useMarketInfo';
import { useRegistrationStore } from '../store/store';
import { FieldType, TDepositLimitsField, TInputField } from '../types/types';
import { DEPOSIT_LIMIT_FIELD_MAX_LENGTH, TEST_ID } from '../utils/constants';
import { currencyByCountryCode } from '../utils/countries';

type DepositLimitKeys = 'daily' | 'weekly' | 'monthly';

type DepositLimitsProps = TDepositLimitsField & {
  autoShowInput?: boolean;
  onBlur?: (field: Partial<TInputField>) => void;
};

const DepositLimits: FC<DepositLimitsProps> = ({
  selections,
  onBlur,
  autoShowInput = true,
}) => {
  const {
    errors,
    setErrors,
    formData,
    setFormData,
    metadata,
    setMetadata,
    setCustomValidations,
  } = useRegistrationStore();

  const { countryCode } = useMarketInfo();
  const { validateLimits } = useValidationLimits();
  const getErrorByKey = useFetchErrorByKey;

  const limitRequiredError = getErrorByKey('limit_required');
  const currency = currencyByCountryCode[countryCode] || 'EUR';

  const initialState = useMemo((): Record<DepositLimitKeys, string> => ({
    daily:   '',
    weekly:  '',
    monthly: '',
  }), []);

  const resetValidationErrors = useCallback(() => {
    setCustomValidations(initialState);
    setErrors(initialState);
    setFormData(initialState);
  }, [initialState, setCustomValidations, setErrors, setFormData]);

  const [selectedLimit, setSelectedLimit] = useState<DepositLimitKeys | null>(
    metadata?.depositType ?? (autoShowInput ? 'daily' : null),
  );

  const handleSelectionChange = useCallback((name: DepositLimitKeys) => {
    resetValidationErrors();
    setCustomValidations({ ...initialState, [name]: limitRequiredError });
    setSelectedLimit(name);
    setMetadata({ depositType: name, limitType: name.toUpperCase() });
  }, [initialState, limitRequiredError, resetValidationErrors, setCustomValidations, setMetadata]);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (['daily', 'weekly', 'monthly'].includes(name)) {
      if (value.length > DEPOSIT_LIMIT_FIELD_MAX_LENGTH) return;

      setCustomValidations(initialState);
      setErrors({ ...initialState, [name]: '' });
      setFormData({ ...initialState, [name]: value });
    }
  }, [initialState, setCustomValidations, setErrors, setFormData]);

  const handleBlur = useCallback(async (item: TInputField) => {
    const limitName = item.name as DepositLimitKeys;

    if (!formData?.[limitName]) {
      setCustomValidations({
        ...initialState,
        [limitName]: limitRequiredError,
      });
      onBlur?.({ name: item.name });

      return;
    }

    await validateLimits(limitName);
    onBlur?.({ name: item.name });
  }, [formData, initialState, limitRequiredError, onBlur, setCustomValidations, validateLimits]);

  const handleClear = (name: DepositLimitKeys) => {
    setFormData({ [name]: '' });
  }

  useEffect(() => {
    const initialValidation =
      formData?.daily || formData?.monthly || formData?.weekly
        ? { ...initialState, daily: '', weekly: '', monthly: '' }
        : { daily: limitRequiredError, weekly: limitRequiredError, monthly: limitRequiredError };

    setCustomValidations(initialValidation);
  }, [formData?.daily, initialState, setCustomValidations, limitRequiredError]);

  return (
    <>
      <Selector name="depositLimitTypeSelector" className="p-[2px]">
        {selections.map(({ label, name }) => (
          <Selector.Radio
            key={name}
            className="text-center has-[:checked]:bg-purple-0 has-[:checked]:text-purple-80"
            value={name}
            name={name}
            checked={selectedLimit === name}
            onChange={() => handleSelectionChange(name as DepositLimitKeys)}
            data-testid={TEST_ID.SELECT + name}
          >
            {label}
          </Selector.Radio>
        ))}
      </Selector>

      {selections.map((item) =>
        selectedLimit === item.name && (
          <Input
            key={item.name}
            id="depositLimit"
            name={item.name}
            type="number"
            inputMode="numeric"
            label={`${item.inputLabel} ${currency}`}
            value={formData?.[item.name as DepositLimitKeys] ?? ''}
            onChange={handleInputChange}
            onBlur={() => handleBlur({ ...item, fieldType: FieldType.Input })}
            autoFocus
            errorMessage={errors[selectedLimit]}
            error={Boolean(errors[selectedLimit])}
            data-error={Boolean(errors[selectedLimit])}
            clearable
            onClear={() => handleClear(item.name)}
            data-testid={TEST_ID.INPUT + 'depositLimit'}
            maxLength={DEPOSIT_LIMIT_FIELD_MAX_LENGTH}
          />
        ),
      )}
    </>
  );
};

export default memo(DepositLimits);
